import { memo, useEffect, useState, useCallback } from 'react';
import { isEmpty, isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { EfficiencyMetric, EfficiencyReport } from 'types';
// Import Components
import { BarChart, BusyLoader, Empty, KeyValue } from '@geneui/components';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useMetricsList } from 'hooks/useMetricsList';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Services
import { PromotionHttpService } from 'services/http';
// Import Constants
import { l } from 'constants/common';
import { INITIAL_CHART_DATA } from './constants';
// Import Helpers
import { customMomentWithTimezoneConversion } from 'services/Helpers';
import { chartFormatter, parseEfficiencyReportToChartData } from './helper';
// Import Enums
import { PromotionTypeEnum } from 'enums';
// Import Styles
import styles from './promotionEfficiencyReport.module.scss';

interface Props {
    promotionId: number;
    promotionType: PromotionTypeEnum;
    efficiencyMetric?: Nullable<EfficiencyMetric>;
}

const PromotionEfficiencyReport: React.FC<Props> = ({ promotionId, promotionType, efficiencyMetric }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const { partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();

    const [isLoadingEfficiencyReport, setIsLoadingEfficiencyReport] = useState(true);
    const [efficiencyReportData, setEfficiencyReportData] = useState<Nullable<EfficiencyReport>>(null);
    const [chartData, setChartData] = useState(INITIAL_CHART_DATA);

    const { metricsMap } = useMetricsList();

    const { getPromotionEfficiencyReportRequest } = useCancelableRequests(
        {
            getPromotionEfficiencyReportRequest: PromotionHttpService.getPromotionEfficiencyReport,
            getPromotionEfficiencyMetricsRequest: PromotionHttpService.getPromotionEfficiencyMetrics,
        },
        { takeLatest: ['getPromotionEfficiencyReportRequest'] },
    );

    const getEfficiencyReport = useCallback(() => {
        if (isNil(efficiencyMetric)) {
            setIsLoadingEfficiencyReport(false);
            return;
        }

        setIsLoadingEfficiencyReport(true);
        doGetRequest(getPromotionEfficiencyReportRequest.request, {
            queryString: { promotionId, promotionType },
            successCallback: (data) => {
                setEfficiencyReportData(data ?? null);
                setChartData(parseEfficiencyReportToChartData(data?.Items ?? [], t));
            },
            errorCallback: () => {
                setEfficiencyReportData(null);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoadingEfficiencyReport(false);
            }
        });
    }, [promotionId, promotionType, doGetRequest, getPromotionEfficiencyReportRequest, t, efficiencyMetric]);

    useEffect(() => {
        getEfficiencyReport();
    }, [getEfficiencyReport]);

    const metricName = metricsMap[efficiencyMetric?.MetricId ?? 0] ?? t(l.NA);

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <SectionWithTitle title={t(l.GeneralDetails)}>
                    <div className={styles.infoContainer}>
                        <KeyValue
                            label={t(l.Metric)}
                            value={metricName}
                            appearance="vertical"
                            className={styles.keyValue}
                        />
                        <KeyValue
                            label={t(l.TrackingPeriod)}
                            value={t(l.DaysWithNumber, { days: efficiencyMetric?.AnalyticsPeriod ?? 0 })}
                            appearance="vertical"
                            className={styles.keyValue}
                        />
                        <KeyValue
                            label={isEmpty(efficiencyReportData?.Items) ? t(l.FirstReportDate) : t(l.NextReportDate)}
                            value={
                                efficiencyReportData?.FirstCalculateDate
                                    ? customMomentWithTimezoneConversion(
                                          efficiencyReportData?.FirstCalculateDate,
                                      ).format(partnerDateTimeWithoutSecondsFormat)
                                    : t(l.NA)
                            }
                            appearance="vertical"
                            className={styles.keyValue}
                        />
                    </div>
                </SectionWithTitle>

                <BusyLoader isBusy={isLoadingEfficiencyReport}>
                    <SectionWithTitle title={metricName}>
                        {isNil(efficiencyMetric) || isEmpty(efficiencyReportData?.Items) ? (
                            <div className={styles.emptyContainer}>
                                <Empty title={t(l.NoDataToDisplay)} />
                            </div>
                        ) : (
                            <div className={styles.chartScrollWrapper}>
                                <div
                                    className={styles.chartContainer}
                                    style={{
                                        minWidth:
                                            chartData.categories.length > 5
                                                ? `${chartData.categories.length * 120}px`
                                                : '100%',
                                    }}
                                >
                                    <BarChart
                                        chart={{ type: 'column' }}
                                        series={chartData?.series}
                                        categories={chartData?.categories}
                                        plotOptions={{
                                            column: {
                                                borderRadius: 4,
                                                columnWidth: 40,
                                            },
                                            line: {
                                                marker: { enabled: true },
                                            },
                                            spline: {
                                                marker: { enabled: true },
                                            },
                                        }}
                                        tooltip={{
                                            formatter: function () {
                                                return chartFormatter({ bar: this, metricName, chartData, t });
                                            },
                                        }}
                                        legend={{
                                            enabled: true,
                                            layout: 'horizontal',
                                            align: 'left',
                                        }}
                                        yAxis={[
                                            {
                                                title: {
                                                    text: '',
                                                },
                                                labels: {
                                                    format: `{value} ${chartData?.currency ?? ''}`,
                                                },
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        )}
                    </SectionWithTitle>
                </BusyLoader>
            </div>
        </div>
    );
};

export default memo(PromotionEfficiencyReport);
